import { useSyncExternalStore } from "react";
import type { CaseSection, SimCaseProgress } from "./types";

// Client-side tracking of simulated-patient case generations this browser
// started. Generation runs in the Celery worker; <SimJobWatcher> polls
// GET /tasks/{task_id} for each job and records its progress and outcome here,
// so the progress survives navigating between pages. Unlike the pipeline jobs
// in jobs.ts, several generations can run at once, so jobs are keyed by task.

export interface SimJob {
  taskId: string;
  kind: "generate" | "regenerate";
  label: string; // "HDP Session 3" or the scenario title
  scenarioId: number | null; // known up front for regenerate, on success for generate
  section: CaseSection | null; // regenerate only; null = whole case
  startedAt: number;
  state: string; // Celery state
  progress: SimCaseProgress | null;
  // Set once the task is over.
  outcome: { ok: boolean; error: string | null; finishedAt: number } | null;
}

// A queued task nobody picked up after this long almost always means the
// Celery worker isn't running.
export const SIM_PENDING_TIMEOUT_MS = 5 * 60 * 1000;
// Longer than the worker's 15-minute time limit.
export const SIM_JOB_TIMEOUT_MS = 20 * 60 * 1000;

let jobs: SimJob[] = [];
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function addSimJob(job: Omit<SimJob, "startedAt" | "state" | "progress" | "outcome">, now = Date.now()) {
  jobs = [...jobs.filter((j) => j.taskId !== job.taskId), { ...job, startedAt: now, state: "PENDING", progress: null, outcome: null }];
  emit();
}

export function updateSimJob(taskId: string, patch: Partial<Omit<SimJob, "taskId">>) {
  if (!jobs.some((j) => j.taskId === taskId)) return;
  jobs = jobs.map((j) => (j.taskId === taskId ? { ...j, ...patch } : j));
  emit();
}

export function dismissSimJob(taskId: string) {
  if (!jobs.some((j) => j.taskId === taskId)) return;
  jobs = jobs.filter((j) => j.taskId !== taskId);
  emit();
}

export function useSimJobs() {
  return useSyncExternalStore(subscribe, () => jobs);
}

// Where a job's progress is already shown in the page itself, so the
// floating tray can leave it out there.
export function jobHome(job: SimJob) {
  return job.kind === "generate" ? "/sim-patients/new" : `/sim-patients/${job.scenarioId}/edit`;
}

// 0–100 for the progress bar. Writing is the long stage; within it the bar
// follows the template sections the worker has seen the model reach.
export function jobPercent(job: SimJob) {
  if (job.outcome?.ok) return 100;
  const p = job.progress;
  if (!p) return job.state === "PENDING" ? 0 : 3;
  switch (p.stage) {
    case "sources":
      return 5;
    case "writing": {
      const total = p.sections_total || 1;
      return 10 + Math.round((80 * (p.sections_done ?? 0)) / total);
    }
    case "repairing":
      return 90;
    case "saving":
      return 96;
  }
}
